import React, { useState } from "react";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import GridSettings from "@controls/grid-settings";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import searchSrc from "./assets/search.svg";
import { VID_PLAT_OPTIONS } from "./doc-plat-utils";

const toDayjs = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const MiniSelect = ({ autoFocus, defaultOpen, options, placeholder, value, onChange, width }) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

const FILTER_KIND = {
  KASSA: "kassa",
  VID: "vid",
  DELETED: "deleted"
};

const VID_CHIP_OPTIONS = VID_PLAT_OPTIONS.filter((o) => o.value !== 0);
const DELETED_OPTIONS = [{ value: 1, label: "Показывать" }];

const KIND_LABEL = {
  [FILTER_KIND.KASSA]: "Касса",
  [FILTER_KIND.VID]: "Вид документа",
  [FILTER_KIND.DELETED]: "Удалённые"
};

const PlatsSearchFilter = ({ value, kassas, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);

  const patch = (next) => onChange?.({ ...value, ...next });
  const kassaOptions = (kassas || []).filter((o) => o.value != null && o.value !== 0);
  const kassaLabel = (kassas || []).find((o) => o.value === (value?.idKassa ?? 0))?.label;
  const hasKassa = (value?.idKassa ?? 0) > 0 && Boolean(kassaLabel);
  const vidLabel = VID_CHIP_OPTIONS.find((o) => o.value === (value?.vid ?? 0))?.label;
  const hasVid = Boolean(value?.vid) && Boolean(vidLabel);

  const chips = [];
  if (hasKassa) {
    chips.push({
      key: FILTER_KIND.KASSA,
      kindLabel: KIND_LABEL[FILTER_KIND.KASSA],
      valueLabel: kassaLabel
    });
  }
  if (hasVid) {
    chips.push({
      key: FILTER_KIND.VID,
      kindLabel: KIND_LABEL[FILTER_KIND.VID],
      valueLabel: vidLabel
    });
  }
  if (value?.showDeleted) {
    chips.push({
      key: FILTER_KIND.DELETED,
      kindLabel: KIND_LABEL[FILTER_KIND.DELETED],
      valueLabel: "Показывать"
    });
  }

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = [
    kassaOptions.length > 0
      ? { value: FILTER_KIND.KASSA, label: KIND_LABEL[FILTER_KIND.KASSA] }
      : null,
    { value: FILTER_KIND.VID, label: KIND_LABEL[FILTER_KIND.VID] },
    { value: FILTER_KIND.DELETED, label: KIND_LABEL[FILTER_KIND.DELETED] }
  ].filter((k) => k && !used.has(k.value));
  const canAdd = availableKinds.length > 0;

  const applyKind = (kind, next) => {
    if (kind === FILTER_KIND.KASSA) patch({ idKassa: next || 0 });
    else if (kind === FILTER_KIND.VID) patch({ vid: next || 0 });
    else if (kind === FILTER_KIND.DELETED) patch({ showDeleted: next === 1 });
  };

  const clearKind = (kind) => {
    if (kind === FILTER_KIND.KASSA) patch({ idKassa: 0 });
    else if (kind === FILTER_KIND.VID) patch({ vid: 0 });
    else if (kind === FILTER_KIND.DELETED) patch({ showDeleted: false });
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    if (availableKinds.length === 1 && availableKinds[0].value === FILTER_KIND.DELETED) {
      patch({ showDeleted: true });
      setAdding(false);
      setDraftKind(null);
      return;
    }
    setDraftKind(availableKinds.length === 1 ? availableKinds[0].value : null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
    setEditKind(null);
  };

  const pickDraftKind = (kind) => {
    if (kind === FILTER_KIND.DELETED) {
      patch({ showDeleted: true });
      cancelAdd();
      return;
    }
    setDraftKind(kind);
  };

  const pickDraftValue = (next) => {
    if (editKind) {
      applyKind(editKind, next);
      setEditKind(null);
      return;
    }
    if (draftKind == null) return;
    applyKind(draftKind, next);
    cancelAdd();
  };

  const activeKind = editKind || draftKind;
  const valueOptions =
    activeKind === FILTER_KIND.KASSA
      ? kassaOptions
      : activeKind === FILTER_KIND.VID
        ? VID_CHIP_OPTIONS
        : DELETED_OPTIONS;
  const draftValue =
    editKind === FILTER_KIND.KASSA && hasKassa
      ? value.idKassa
      : editKind === FILTER_KIND.VID && hasVid
        ? value.vid
        : editKind === FILTER_KIND.DELETED
          ? 1
          : undefined;

  return (
    <div className="doc-plats__search" role="group" aria-label="Поиск и фильтр">
      <span className="doc-plats__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="doc-plats__search-input"
        value={value?.q || ""}
        placeholder="ФИО, назначение, комментарий..."
        onChange={(e) => patch({ q: e.target.value })}
      />
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="doc-plats__search-draft">
            <span className="doc-plats__search-chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={valueOptions}
              placeholder="Значение"
              value={draftValue}
              width={160}
              onChange={pickDraftValue}
            />
            <button
              type="button"
              className="doc-plats__search-chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="doc-plats__search-chip">
            <button
              type="button"
              className="doc-plats__search-chip-btn"
              title="Изменить значение"
              onClick={() => {
                setAdding(false);
                setDraftKind(null);
                setEditKind(chip.key);
              }}
            >
              <span className="doc-plats__search-chip-kind">{chip.kindLabel}</span>
              <span className="doc-plats__search-chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="doc-plats__search-chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearKind(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="doc-plats__search-draft">
          {draftKind == null ? (
            <MiniSelect
              autoFocus
              defaultOpen
              options={availableKinds}
              placeholder="Фильтр"
              value={draftKind}
              width={150}
              onChange={pickDraftKind}
            />
          ) : (
            <>
              <span className="doc-plats__search-chip-kind">
                {availableKinds.find((k) => k.value === draftKind)?.label || KIND_LABEL[draftKind]}
              </span>
              <MiniSelect
                autoFocus
                defaultOpen
                options={valueOptions}
                placeholder="Значение"
                width={160}
                onChange={pickDraftValue}
              />
            </>
          )}
          <button
            type="button"
            className="doc-plats__search-chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding && !editKind ? (
        <button
          type="button"
          className="doc-plats__search-add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

/**
 * Фильтры и тулбар журнала платежей. Delphi: TFrmJurnPlat.
 */
const DocPlatsFilters = ({
  value,
  loading,
  canAdd,
  canDelete,
  reportId,
  onChange,
  onCreate,
  onDelete,
  gridColumns,
  gridStorageKey,
  gridSettings,
  gridDefaultSettings,
  onGridSettingsChange,
  kassas
}) => (
  <div className="doc-plats__filters">
    <div className="doc-plats__extra-actions">
      <Tooltip title={canAdd ? "Новый платёж" : "Нет права создавать платежи"}>
        <span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canAdd || loading}
            aria-label="Новый платёж"
            onClick={onCreate}
          />
        </span>
      </Tooltip>
      <Tooltip title={canDelete ? "Удалить" : "Отметьте платежи"}>
        <span>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={!canDelete || loading}
            onClick={onDelete}
          />
        </span>
      </Tooltip>
      <GridSettings
        title="Настройка таблицы"
        columns={gridColumns}
        storageKey={gridStorageKey}
        value={gridSettings}
        defaultSettings={gridDefaultSettings}
        onChange={onGridSettingsChange}
      />
      <ReportButton
        vid={REPORT_VID.PAYMENTS}
        id={reportId}
        begPer={value?.dateBeg}
        endPer={value?.dateEnd}
        disabled={reportId == null}
        compact
      />
    </div>
    <div className="doc-plats__field">
      <DatePicker
        allowClear={false}
        format="DD.MM.YYYY"
        value={toDayjs(value?.dateBeg)}
        suffixIcon={null}
        title="Дата с"
        aria-label="Дата с"
        onChange={(d) =>
          onChange?.({
            ...value,
            dateBeg: d ? d.format("YYYY-MM-DD") : value.dateBeg
          })
        }
      />
    </div>
    <div className="doc-plats__field">
      <DatePicker
        allowClear={false}
        format="DD.MM.YYYY"
        value={toDayjs(value?.dateEnd)}
        suffixIcon={null}
        title="Дата по"
        aria-label="Дата по"
        onChange={(d) =>
          onChange?.({
            ...value,
            dateEnd: d ? d.format("YYYY-MM-DD") : value.dateEnd
          })
        }
      />
    </div>
    <PlatsSearchFilter value={value} kassas={kassas} onChange={onChange} />
  </div>
);

export default DocPlatsFilters;
